import { useState } from "react";
import { View, Text, Pressable } from "react-native";
import { ChevronDown, HelpCircle } from "lucide-react-native";
import { SectionTitle, MoonDivider } from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useRegion, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../onboarding/theme";

const US_FAQ_ITEMS = [
  {
    id: "1",
    question: "What if my insurance doesn’t cover fertility treatments?",
    answer:
      "Many employers offer fertility benefits through programs like Progyny, Carrot, or Maven. We can help you check if you have access to these benefits. Additionally, many clinics offer financing options and payment plans to make treatment more accessible.",
  },
  {
    id: "2",
    question: "How long does insurance verification take?",
    answer:
      "Verification typically takes 1–2 business days. We contact your insurance provider directly to confirm your coverage details, including deductibles, copays, and any fertility-specific benefits. You’ll receive a notification once verification is complete.",
  },
  {
    id: "3",
    question: "Can I use HSA or FSA for fertility treatments?",
    answer:
      "Yes! Most fertility treatments, medications, and diagnostic tests are eligible expenses for Health Savings Accounts (HSA) and Flexible Spending Accounts (FSA). Check with your plan administrator for specific details.",
  },
  {
    id: "4",
    question: "How accurate are the cost estimates?",
    answer:
      "Our estimates use U.S. national average pricing data from CareCredit (2025) and FertilityIQ. Actual costs vary by clinic, location, and insurance coverage. When you add your insurance, we refine the estimate using your plan’s deductible, coinsurance, and out-of-pocket maximum.",
  },
];

const FR_FAQ_ITEMS = [
  {
    id: "fr-1",
    question: "Quelle est la prise en charge de l'AMP en France ?",
    answer:
      "L'Assurance Maladie prend en charge à 100 % jusqu'à 4 tentatives de FIV et 6 inséminations artificielles avant l'âge de 43 ans pour la personne portant l'enfant. Votre Mutuelle peut couvrir les éventuels restes à charge.",
  },
  {
    id: "fr-2",
    question: "Comment Louise peut-il m'aider aujourd'hui en France ?",
    answer:
      "Vous pouvez utiliser Louise pour suivre votre cycle, organiser vos rendez-vous et documents médicaux, et accéder à des ressources éducatives sur la fertilité.",
  },
  {
    id: "fr-3",
    question: "Qui peut accéder à l'AMP en France ?",
    answer:
      "Depuis la loi de bioéthique du 2 août 2021, l'AMP est accessible aux couples hétérosexuels, aux couples de femmes et aux femmes seules. Une infertilité médicalement constatée n'est plus requise pour les couples de femmes et les femmes seules. La consultation initiale a lieu dans un centre d'AMP agréé, public ou privé.",
  },
  {
    id: "fr-4",
    question: "Faut-il faire une demande préalable à la Sécurité sociale ?",
    answer:
      "Oui. Avant de commencer un parcours d'AMP, votre centre établit une demande d'entente préalable (formulaire S3140) auprès de l'Assurance Maladie. Une fois accordée, vos soins liés à l'AMP sont pris en charge à 100 % sur la base du tarif de la Sécurité sociale.",
  },
  {
    id: "fr-5",
    question: "Quel est l'âge limite pour bénéficier de l'AMP en France ?",
    answer:
      "L'Assurance Maladie prend en charge les soins d'AMP jusqu'au 43e anniversaire de la personne qui portera l'enfant, et jusqu'au 60e anniversaire du membre du couple qui ne portera pas l'enfant. Au-delà, les soins ne sont plus remboursés.",
  },
];

export default function FaqSection() {
  const region = useRegion();
  const t = useTranslations();
  const capturePress = usePressCapture("FaqSection");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const items = region === "US" ? US_FAQ_ITEMS : FR_FAQ_ITEMS;

  return (
    <View>
      <View style={{ marginBottom: 12, paddingHorizontal: 2 }}>
        <SectionTitle icon={HelpCircle}>
          {t("faq.frequently_asked")}
        </SectionTitle>
      </View>

      <View style={{ ...(GLASS_CARD_STYLE as object), padding: 18 }}>
        {items.map((item, index) => {
          const isExpanded = expandedId === item.id;
          const isLast = index === items.length - 1;

          return (
            <View key={item.id}>
              <Pressable
                onPress={() => {
                  // The question copy is region-specific (US/FR lists), so the
                  // stable identifier is the control, not the visible string.
                  capturePress(pressLabel(item.question), "faq_item");
                  setExpandedId(isExpanded ? null : item.id);
                }}
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  paddingVertical: 12,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "800",
                    color: EMBER.colors.text,
                    flex: 1,
                    paddingRight: 12,
                    lineHeight: 18,
                  }}
                >
                  {item.question}
                </Text>
                <View
                  style={{
                    transform: [{ rotate: isExpanded ? "180deg" : "0deg" }],
                    marginTop: 3,
                  }}
                >
                  <ChevronDown
                    size={14}
                    color={EMBER.colors.textFaint}
                    strokeWidth={1.5}
                  />
                </View>
              </Pressable>

              {isExpanded && (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: EMBER.colors.textLight,
                    lineHeight: 18,
                    paddingTop: 2,
                    paddingBottom: 10,
                  }}
                >
                  {item.answer}
                </Text>
              )}

              {!isLast && <MoonDivider color="rgba(255,255,255,0.12)" />}
            </View>
          );
        })}
      </View>
    </View>
  );
}
